import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { usePropsContext } from '@uhg-abyss/web/hooks/usePropsContext';
import { UHCCountdown } from '@sde/widgets-core/src/common/UHCCountdown';
import { Modal } from '@uhg-abyss/web/ui/Modal';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Button } from '@uhg-abyss/web/ui/Button';
import { storage } from '@uhg-abyss/web/tools/storage';
import { RESET_TIME, HAS_SIGNED_OUT } from '../constants';
import { event } from '@uhg-abyss/web/tools/event';

const StyledModalSection1 = styled(Modal.Section, {
  borderTop: '1px solid $black',
  borderBottom: '1px solid $gray3',
});

const StyledModalSection2 = styled(Modal.Section, {
  textAlign: 'center',
  padding: '0px',
});

const StyledModalText = styled('div', {
  margin: '5px 0 12px',
  lineHeight: '1.2',
});

export const IdleModal = ({
  timeRemaining,
  pingInterval,
  isOpen,
  closeModal,
}) => {
  const { data, events } = usePropsContext();
  const [originalExpiredTime, setOriginalExpiredTime] = useState();

  useEffect(() => {
    setOriginalExpiredTime(storage.local.get(RESET_TIME));
  }, [isOpen]);

  const handleLogout = () => {
    storage.local.set(HAS_SIGNED_OUT, true);
    events('onSignout');
    event('IDLE_MODAL_CLICK', {
      interactionName: 'Session Timeout',
      interactionValue: 'Sign Out',
      interactionContext: 'Session Timeout Warning Modal',
    });
    event('IDLE_MODAL_CLOSE', {
      modalName: 'Session Timeout Modal',
    });
  };

  const handleTimeout = () => {
    if (data.idleStorageKey) {
      if (!events.setIdleRecovery || events('setIdleRecovery')) {
        storage.local.set(data.idleStorageKey, window.location.href);
      }
    }

    if (events.onIdleTimeout) {
      events('onIdleTimeout');
    } else {
      handleLogout();
    }
  };

  const handleTick = (timeMS) => {
    if (originalExpiredTime !== storage.local.get(RESET_TIME)) {
      closeModal();
    }

    if (storage.local.get(HAS_SIGNED_OUT)) {
      events('onSignout');
    }
    const tickInterval = Math.floor((timeMS % pingInterval) / 1000);

    if (tickInterval === 0) {
      events('onIdleTick', timeMS);
    }
  };

  return (
    <Modal
      size={600}
      isOpen={isOpen}
      closeOnClickOutside={false}
      onClose={closeModal}
      title='Session Timeout Warning'
      data-testid='idle-modal'
      footer={
        <Modal.Section>
          <Layout.Group alignLayout='center'>
            <Button
              css={{ 'min-width': '200px' }}
              variant='outline'
              onClick={handleLogout}
              role='button'
            >
              Sign Out
            </Button>
            <Button
              css={{ 'min-width': '200px' }}
              role='button'
              onClick={closeModal}
            >
              Continue Working
            </Button>
          </Layout.Group>
        </Modal.Section>
      }
    >
      <StyledModalSection1>
        <StyledModalSection2>
          <StyledModalText>
            You will be signed out in a few minutes due to inactivity.
          </StyledModalText>
          <StyledModalText>
            All unsaved data on this page will be lost.
          </StyledModalText>
          <StyledModalText>
            Time remaining:{' '}
            <UHCCountdown
              countdownMS={timeRemaining}
              handleTick={handleTick}
              onComplete={handleTimeout}
            />
          </StyledModalText>
        </StyledModalSection2>
      </StyledModalSection1>
    </Modal>
  );
};

IdleModal.propTypes = {
  timeRemaining: PropTypes.number,
  pingInterval: PropTypes.number,
  isOpen: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
};

IdleModal.defaultProps = {
  timeRemaining: 6000,
  pingInterval: 3000,
};
